import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../../ui/components/Button";
import { Stepper } from "../../../../../../ui/components/Stepper";
import { FeatherUpload, FeatherCheckCircle, FeatherXCircle, FeatherArrowLeft, FeatherAlertCircle, FeatherPlus } from "@subframe/core";
import { SavedPivotView, ExcelData, ExcelColumn } from "../types";
import { loadPivotView } from "../utils/storage";
import { mapColumns, validateColumnMapping, ColumnMatch, compareColumns, ColumnComparisonResult } from "../utils/columnMapping";
import { PeriodLeadModal } from "./PeriodLeadModal";
import { Step3Results } from "./Step3Results";
import { ColumnComparisonPanel } from "./ColumnComparisonPanel";
import { PivotTableData } from "../types";
import { uploadExcelFile, generatePivotTable, getPivotView, updatePivotViewLastUsed, getPivotViews } from "../../../../data/api/pivotApi";
import { useAuth } from "../../../../../auth";

interface ApplyPivotViewFlowProps {
  viewId: string;
}

type Step = 2 | 3;

export const ApplyPivotViewFlow: React.FC<ApplyPivotViewFlowProps> = ({ viewId }) => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>(2);
  const [savedView, setSavedView] = useState<SavedPivotView | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedLead, setSelectedLead] = useState<string>("");
  const [showPeriodLeadModal, setShowPeriodLeadModal] = useState<boolean>(false);
  const [columnMatches, setColumnMatches] = useState<ColumnMatch[]>([]);
  const [mappingValidation, setMappingValidation] = useState<{ isValid: boolean; missingColumns: string[]; warnings: string[] } | null>(null);
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [pivotTableData, setPivotTableData] = useState<Record<string, PivotTableData>>({});
  const [allViews, setAllViews] = useState<SavedPivotView[]>([]); // All views from same file
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [fileId, setFileId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // New columns detection state
  const [columnComparison, setColumnComparison] = useState<ColumnComparisonResult | null>(null);
  const [selectedNewColumns, setSelectedNewColumns] = useState<Set<string>>(new Set());
  const [showCreateNewPivotFlow, setShowCreateNewPivotFlow] = useState(false);

  // Load saved view and all related views from same file
  useEffect(() => {
    const loadView = async () => {
      try {
        // Try to load from API first
        const view = await getPivotView(viewId);
        setSavedView(view);
        // Pre-fill period and lead if available
        if (view.lastPeriod) setSelectedPeriod(view.lastPeriod);
        if (view.lastLead) setSelectedLead(view.lastLead);
        
        // Load all views from the same file
        // TypeScript workaround: fileId is optional in SavedPivotView but may not be recognized
        const viewFileId = 'fileId' in view ? (view as any).fileId : undefined;
        if (viewFileId) {
          const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
          const relatedViews = await getPivotViews(companyId, viewFileId);
          console.log(`Loaded ${relatedViews.length} views from file_id: ${viewFileId}`, relatedViews);
          setAllViews(relatedViews);
        } else {
          // If no fileId, just use the single view
          console.log('No fileId found on view, using single view only');
          setAllViews([view]);
        }
        
        // Sync to localStorage for offline support
        const { syncToLocalStorage } = await import("../utils/storage");
        syncToLocalStorage([view]);
      } catch (apiError: any) {
        console.warn("Failed to load view from API, trying localStorage:", apiError);
        // Fallback to localStorage
        const view = loadPivotView(viewId);
        if (!view) {
          alert("Pivot view not found. Redirecting to library...");
          navigate("/cfo/registers/uploadv1");
          return;
        }
        setSavedView(view);
        setAllViews([view]);
        // Pre-fill period and lead if available
        if (view.lastPeriod) setSelectedPeriod(view.lastPeriod);
        if (view.lastLead) setSelectedLead(view.lastLead);
      }
    };
    loadView();
  }, [viewId, navigate, company?.companyId]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !["xlsx", "xls"].includes(extension)) {
      setUploadError("Invalid file type. Only .xlsx and .xls files are allowed.");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File size exceeds maximum limit of 10MB.");
      return;
    }

    setUploadedFile(file);
    setUploadError("");
    setIsUploading(true);

    try {
      // Upload and parse Excel file using the API
      const result = await uploadExcelFile(file, selectedPeriod || undefined, selectedLead || undefined);
      
      // Set the parsed columns
      setExcelColumns(result.columns);
      setFileId(result.fileId);
      
      // Create ExcelData object
      const excelDataObj: ExcelData = {
        headers: result.headers,
        rows: result.sampleRows || [],
        fileId: result.fileId,
        columns: result.columns,
      };
      setExcelData(excelDataObj);

      // Map columns from saved view to new Excel columns
      if (savedView) {
        // Aggregate column mappings from ALL views in the register set
        // This ensures we compare against the latest columns (including new columns from previous uploads)
        // Use originalName (case-insensitive) as key since that's what we compare against
        const allColumnMappings = new Map<string, { originalName: string; columnId: string; type: string; category: string }>();
        
        // Start with the primary view's column mappings
        for (const mapping of savedView.columnMappings) {
          const key = mapping.originalName.toLowerCase().trim();
          allColumnMappings.set(key, mapping);
        }
        
        // Add column mappings from all other views in the register set
        // This includes columns from tabs created with new columns in previous uploads
        for (const view of allViews) {
          if (view.columnMappings) {
            for (const mapping of view.columnMappings) {
              // Use originalName (case-insensitive) as key to avoid duplicates
              // This matches how detectNewColumns compares columns
              const key = mapping.originalName.toLowerCase().trim();
              if (!allColumnMappings.has(key)) {
                allColumnMappings.set(key, mapping);
              }
            }
          }
        }
        
        // Convert map back to array
        const aggregatedColumnMappings = Array.from(allColumnMappings.values());
        
        // Use aggregated column mappings for comparison
        const matches = mapColumns(aggregatedColumnMappings, result.columns);
        setColumnMatches(matches);
        const validation = validateColumnMapping(matches);
        setMappingValidation(validation);
        
        // Compare columns to detect new ones (using aggregated mappings)
        const comparison = compareColumns(aggregatedColumnMappings, result.columns);
        setColumnComparison(comparison);
        
        // Reset selected new columns
        setSelectedNewColumns(new Set());
      }

      // Always show period/lead modal after file selection to confirm/change values
      setShowPeriodLeadModal(true);
    } catch (error: any) {
      console.error("Failed to upload Excel file:", error);
      setUploadError(error.message || "Failed to upload and parse Excel file. Please try again.");
      setUploadedFile(null);
      setExcelColumns([]);
      setFileId(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePeriodLeadConfirm = (period: string, lead: string) => {
    setSelectedPeriod(period);
    setSelectedLead(lead);
    setShowPeriodLeadModal(false);
    setUploadError("");
  };

  const handleApply = async () => {
    if (!uploadedFile || !selectedPeriod || !selectedLead) {
      setUploadError("Please upload a file and select period and lead.");
      return;
    }

    if (!mappingValidation?.isValid) {
      setUploadError("Cannot apply configuration: Some required columns are missing.");
      return;
    }

    if (!fileId || !savedView) {
      setUploadError("Missing file or configuration data. Please try uploading again.");
      return;
    }

    setIsGenerating(true);
    setUploadError("");

    try {
      // Update last used (including the fileId) for all views from the same file
      for (const view of allViews) {
        await updatePivotViewLastUsed(view.id, selectedPeriod, selectedLead, fileId);
      }

      // Generate pivot table for all views from the same file
      const viewsToGenerate = allViews.length > 0 ? allViews : [savedView];
      const allPivotData: Record<string, PivotTableData> = {};
      
      for (const view of viewsToGenerate) {
        try {
          const pivotData = await generatePivotTable(
            fileId,
            view.config,
            selectedPeriod,
            selectedLead
          );
          allPivotData[view.id] = pivotData;
        } catch (err) {
          console.warn(`Failed to generate pivot for view ${view.id}:`, err);
        }
      }

      setPivotTableData(allPivotData);
      setCurrentStep(3);
    } catch (error: any) {
      console.error("Failed to generate pivot table:", error);
      setUploadError(error.message || "Failed to generate pivot table. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBack = () => {
    navigate("/cfo/registers/uploadv1");
  };

  // Handlers for new column selection
  const handleToggleNewColumn = (columnId: string) => {
    setSelectedNewColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  };

  const handleSelectAllNewColumns = () => {
    if (columnComparison) {
      setSelectedNewColumns(new Set(columnComparison.newColumns.map(col => col.id)));
    }
  };

  const handleDeselectAllNewColumns = () => {
    setSelectedNewColumns(new Set());
  };

  const handleCreateNewPivotView = () => {
    if (!fileId || selectedNewColumns.size === 0 || !savedView) return;
    
    // Get the selected new column IDs (to highlight as "NEW")
    const newColumnIds = columnComparison?.newColumns
      .filter(col => selectedNewColumns.has(col.id))
      .map(col => col.id) || [];
    
    // Get the parent's file_id (the register set identifier for linking)
    const parentFileId = savedView.fileId || "";
    
    // Navigate to create page in "add to register" mode
    // This will load existing tabs and show all columns with new ones highlighted
    const params = new URLSearchParams({
      dataFileId: fileId, // The uploaded data file
      newColumnIds: JSON.stringify(newColumnIds), // Columns to highlight as NEW
      parentFileId: parentFileId, // The register set's file_id (for loading existing tabs)
      mode: "add-to-register", // Special mode flag
      period: selectedPeriod || "", // Pass the selected period
      lead: selectedLead || "", // Pass the selected lead
    });
    navigate(`/cfo/registers/uploadv1/create?${params.toString()}`);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 2:
        return (
          <div className="flex flex-col gap-6 p-6 min-h-full w-full">
            <div>
              <h2 className="text-heading-2 font-heading-2 text-default-font mb-2">
                Upload Monthly Data
              </h2>
              <p className="text-body font-body text-subtext-color">
                Upload the Excel file for the new month
              </p>
            </div>

            {/* Configuration Summary - Show ALL configurations */}
            {allViews.length > 0 && (
              <div className="rounded-md border border-neutral-border overflow-hidden">
                <div className="p-4 bg-neutral-50 border-b border-neutral-border">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-body-bold font-body-bold text-default-font">
                      Configurations ({allViews.length} {allViews.length === 1 ? 'tab' : 'tabs'})
                    </h3>
                  </div>
                  
                  {/* Show all configurations in a grid */}
                  <div className={`grid gap-3 ${allViews.length === 1 ? 'grid-cols-1' : allViews.length === 2 ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                    {allViews.map((view, index) => (
                      <div 
                        key={view.id} 
                        className="p-3 bg-white rounded-md border border-neutral-200 hover:border-brand-300 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 flex items-center justify-center bg-brand-100 text-brand-700 text-xs font-bold rounded-full">
                            {index + 1}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font truncate">
                            {view.name}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-caption font-caption text-subtext-color">Row Groups: </span>
                            <span className="text-caption text-default-font">
                              {view.config.rowGroups.length > 0
                                ? view.config.rowGroups.map((f) => f.name).join(", ")
                                : "None"}
                            </span>
                          </div>
                          <div>
                            <span className="text-caption font-caption text-subtext-color">Values: </span>
                            <span className="text-caption text-default-font">
                              {view.config.values.length > 0
                                ? view.config.values.map((f) => f.name).join(", ")
                                : "None"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* File Upload */}
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
              <Button
                variant="brand-primary"
                icon={<FeatherUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? "Uploading..." : "Choose Excel File"}
              </Button>
              {uploadedFile && !isUploading && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-brand-50 border border-brand-200">
                  <FeatherCheckCircle className="text-brand-600" />
                  <span className="text-body font-body text-default-font">
                    {uploadedFile.name}
                  </span>
                </div>
              )}
            </div>

            {/* Period & Lead Selection */}
            {uploadedFile && (selectedPeriod || selectedLead) && (
              <div className="p-4 border border-neutral-border rounded-lg bg-neutral-50">
                <div className="flex items-center gap-4 mb-2">
                  {selectedPeriod && (
                    <div>
                      <span className="text-caption font-caption text-subtext-color">Period: </span>
                      <span className="text-body font-body text-default-font">{selectedPeriod}</span>
                    </div>
                  )}
                  {selectedLead && (
                    <div>
                      <span className="text-caption font-caption text-subtext-color">Lead: </span>
                      <span className="text-body font-body text-default-font">{selectedLead}</span>
                    </div>
                  )}
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    onClick={() => setShowPeriodLeadModal(true)}
                  >
                    Change
                  </Button>
                </div>
              </div>
            )}

            {/* Column Comparison Panel */}
            {columnComparison && (
              <ColumnComparisonPanel
                comparison={columnComparison}
                selectedNewColumns={selectedNewColumns}
                onToggleNewColumn={handleToggleNewColumn}
                onSelectAllNewColumns={handleSelectAllNewColumns}
                onDeselectAllNewColumns={handleDeselectAllNewColumns}
              />
            )}

            {/* Error Message */}
            {uploadError && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-error-200">
                <FeatherXCircle className="text-error-600" />
                <span className="text-body font-body text-error-700">{uploadError}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button variant="neutral-tertiary" icon={<FeatherArrowLeft />} onClick={() => navigate("/cfo/registers/uploadv1")}>
                Back to Library
              </Button>
              <div className="flex items-center gap-3">
                {/* Create New Pivot View Button - shown when new columns are selected */}
                {selectedNewColumns.size > 0 && columnComparison?.isValid && (
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherPlus />}
                    onClick={handleCreateNewPivotView}
                    disabled={isGenerating}
                  >
                    Create New Pivot View ({selectedNewColumns.size} columns)
                  </Button>
                )}
                <Button
                  variant="brand-primary"
                  onClick={handleApply}
                  disabled={!uploadedFile || !selectedPeriod || !selectedLead || !columnComparison?.isValid || isGenerating}
                >
                  {isGenerating ? "Generating..." : "Apply Configuration & View Results"}
                </Button>
              </div>
            </div>

            {/* Period & Lead Modal */}
            <PeriodLeadModal
              isOpen={showPeriodLeadModal}
              onClose={() => setShowPeriodLeadModal(false)}
              onConfirm={handlePeriodLeadConfirm}
              initialPeriod={selectedPeriod}
              initialLead={selectedLead}
              leadReadOnly={true}
            />
          </div>
        );

      case 3:
        return (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto">
              {savedView && (
                <Step3Results
                  pivotConfig={savedView.config}
                  pivotData={pivotTableData[savedView.id] || null}
                  viewName={savedView.name}
                  viewId={viewId} // Pass the saved view ID for export
                  fileId={fileId || undefined} // Pass fileId if available (for unsaved exports)
                  allTabs={allViews.length > 0 ? allViews.map(view => ({
                    id: view.id,
                    name: view.name,
                    config: view.config,
                    createdAt: new Date(view.createdAt),
                    updatedAt: new Date(view.updatedAt),
                    savedViewId: view.id,
                    isSaved: true,
                  })) : []} // Pass all views from same file as tabs
                  allTabsData={pivotTableData} // Pass all pivot data for previews
                  onBack={() => setCurrentStep(2)}
                  onEditView={() => navigate(`/cfo/registers/uploadv1/edit/${viewId}`)}
                  onCreateNewView={() => navigate("/cfo/registers/uploadv1/create")}
                />
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4 px-8 py-4 border-b border-neutral-border bg-default-background w-full">
        <Button variant="neutral-tertiary" icon={<FeatherArrowLeft />} onClick={handleBack}>
          Back to Library
        </Button>
        <div>
          <h1 className="text-heading-1 font-heading-1 text-default-font">
            Apply Pivot View
          </h1>
          {savedView && (
            <p className="text-body font-body text-subtext-color">
              {savedView.name}
            </p>
          )}
        </div>
      </div>

      {/* Stepper */}
      <div className="w-full px-8 py-4 border-b border-neutral-border bg-default-background">
        <Stepper>
          <Stepper.Step
            variant={currentStep === 2 ? "active" : currentStep > 2 ? "completed" : "default"}
            stepNumber="1"
            label="Upload Data"
            firstStep
          />
          <Stepper.Step
            variant={currentStep === 3 ? "active" : "default"}
            stepNumber="2"
            label="View Results"
            lastStep
          />
        </Stepper>
      </div>

      {/* Step Content */}
      <div className="flex-1 w-full overflow-auto bg-default-background">
        {renderStepContent()}
      </div>
    </div>
  );
};

